import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, PauseCircle, X, Warehouse, IndianRupee, Paperclip,
  ShieldAlert, Clock,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * StoresPOActionModal — Phase 10, `stores_po_review` stage.
 * Approvers: CAO or Arun.D (one-of gate; either can act).
 *
 * Rules:
 *   • Approve  → `stores_approx_price` (alphanumeric, required),
 *                `procurement_tat` (dropdown, required),
 *                3 Quote slots. If approx_cost parses > 9999 → all 3 required.
 *                Attachment Remark (min 10 chars) mandatory when any quote uploaded.
 *                Remarks optional.
 *   • Hold/Reject → remarks mandatory (min 2 chars); price/quotes/TAT ignored.
 */
const PRICE_RE = /^[A-Za-z0-9 ,.\-₹$/()+]+$/;
const TAT_OPTIONS = [
  "1 day", "2 days", "3 days", "4 days", "5 days",
  "7 days", "10 days", "15 days", "1 month", "long term / vacation",
];

function _parseCost(price) {
  const digits = String(price || "").replace(/[^0-9.]/g, "");
  if (!digits) return null;
  const n = parseFloat(digits);
  return Number.isFinite(n) ? n : null;
}

export default function StoresPOActionModal({
  open, onClose, onDone, requisition, action, actingAsBanner = null,
}) {
  const [remarks, setRemarks] = useState("");
  const [price, setPrice] = useState("");
  const [tat, setTat] = useState("");
  const [attachmentRemark, setAttachmentRemark] = useState("");
  const [files, setFiles] = useState([null, null, null]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRemarks(""); setPrice(""); setTat(""); setAttachmentRemark("");
    setFiles([null, null, null]); setError("");
    setTimeout(() => textareaRef.current?.focus(), 20);
  }, [open, action, requisition?.ref_code]);

  const cfg = useMemo(() => ({
    approve: { label: "Approve", pastTense: "Approved", Icon: CheckCircle2, tone: "teal",
               hint: "Confirm the approximate cost, procurement TAT and (when needed) attach 3 vendor quotes." },
    on_hold: { label: "Place on Hold", pastTense: "On-hold", Icon: PauseCircle, tone: "gold",
               hint: "The applicant will be asked to revise the requisition." },
    reject:  { label: "Reject", pastTense: "Rejected", Icon: XCircle, tone: "danger",
               hint: "The requisition will be closed with your reason." },
  }[action]), [action]);

  if (!open || !requisition || !cfg) return null;

  const trimmedRemarks = remarks.trim();
  const trimmedPrice = price.trim();
  const trimmedAttRemark = attachmentRemark.trim();
  const remarksMandatory = false;   // Phase 12.1 — no min-length on status remarks.
  const remarksOk = true;
  // Phase 12.1 — Approx Cost + procurement_tat + quotes all OPTIONAL (vendor
  // list captures pricing separately). Only shape check on price when present.
  const priceOk = action !== "approve"
    ? true
    : (trimmedPrice.length === 0 || PRICE_RE.test(trimmedPrice));
  const filledFiles = files.filter(Boolean);
  const attRemarkOk = action !== "approve"
    ? true
    : (filledFiles.length > 0 ? trimmedAttRemark.length >= 10 : true);
  const canSubmit = priceOk && attRemarkOk && !busy;

  const btnCls = {
    teal:   "bg-[#2d6b6b] hover:bg-[#1f4c4c] border-[#1f4c4c]",
    gold:   "bg-[#b48a45] hover:bg-[#8a6a2f] border-[#8a6a2f]",
    danger: "bg-[#b3261e] hover:bg-[#8a1c17] border-[#8a1c17]",
  }[cfg.tone];

  const setFileAt = (i, file) => setFiles((prev) => {
    const copy = [...prev]; copy[i] = file || null; return copy;
  });

  const uploadOne = async (file, slotIdx) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("field_name", `stores_quote_${slotIdx + 1}`);
    const { data } = await api.post("/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return {
      field_name: `stores_quote_${slotIdx + 1}`,
      filename: data.filename, url: data.url, size: data.size,
      kind: "procurement_quote",
    };
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError("");
    try {
      const payload = { action, remarks: trimmedRemarks };
      if (action === "approve") {
        payload.stores_approx_price = trimmedPrice;
        payload.procurement_tat = tat;
        if (trimmedAttRemark) payload.attachment_remark = trimmedAttRemark;
        const uploaded = [];
        for (let i = 0; i < 3; i++) {
          if (files[i]) {
            try {
              const att = await uploadOne(files[i], i);
              uploaded.push(att);
            } catch {
              toast.warning(`Quote ${i + 1} upload failed.`);
              throw new Error(`Quote ${i + 1} upload failed. Please retry.`);
            }
          }
        }
        if (uploaded.length) payload.attachments = uploaded;
      }
      const { data } = await api.post(`/requisitions/${requisition.ref_code}/action`, payload);
      toast.success(`${cfg.pastTense} — ${requisition.ref_code}`);
      onDone?.(data);
      onClose?.();
    } catch (err) {
      const msg = err?.message?.startsWith?.("Quote") ? err.message : formatApiError(err);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
         onClick={onClose} data-testid="stores-po-modal">
      <div className="w-full max-w-xl bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden max-h-[92vh] flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-white">
          <div className="flex items-center gap-2">
            <Warehouse className="text-[#2d6b6b]" size={18} />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              Stores PO · {cfg.label} — {requisition.ref_code}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f4e9eb]" aria-label="Close">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          {actingAsBanner && (
            <div className="text-xs text-[#7a1f2b] bg-[#faf5eb] border border-[#b48a45] rounded px-3 py-2 flex items-start gap-2"
                 data-testid="super-admin-acting-as-banner">
              <ShieldAlert size={14} className="mt-0.5 shrink-0 text-[#b48a45]" />
              <div>You are acting as <strong>{actingAsBanner}</strong> on behalf of the assigned approver. Logged as a super-admin bypass.</div>
            </div>
          )}
          <div className="text-xs text-[#4a524c] bg-[#faf5eb] border-l-4 border-[#2d6b6b] px-3 py-2 rounded">
            <strong>{cfg.hint}</strong>
          </div>
          {error && (
            <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded" data-testid="modal-server-error">{error}</div>
          )}

          {action === "approve" && (
            <>
              <label className="block">
                <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                  Approx Cost (Rs) <span className="text-[#b3261e]">*</span>
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">alphanumeric</span>
                </span>
                <div className="relative">
                  <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6e7770]" />
                  <input type="text" value={price}
                         onChange={(e) => setPrice(e.target.value.slice(0, 120))}
                         placeholder="e.g. ₹ 15,000"
                         className={`input-vgws pl-9 ${trimmedPrice && !priceOk ? "!border-[#b3261e]" : (trimmedPrice && priceOk) ? "!border-[#2d6b6b]" : ""}`}
                         data-testid="stores-approx-cost" />
                </div>
                {trimmedPrice && !priceOk && (
                  <div className="mt-1 text-xs text-[#b3261e]">Only alphanumeric plus space and ,.-₹$/()+ are allowed.</div>
                )}
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                  Procurement TAT <span className="text-[#b3261e]">*</span>
                </span>
                <div className="relative">
                  <Clock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6e7770]" />
                  <select value={tat} onChange={(e) => setTat(e.target.value)}
                          className={`input-vgws pl-9 ${tat ? "!border-[#2d6b6b]" : ""}`}
                          data-testid="procurement-tat">
                    <option value="">Select expected TAT…</option>
                    {TAT_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </label>

              <div>
                <div className="text-sm font-medium text-[#1f2320] mb-2">
                  Procurement Quotation Attachments
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">optional</span>
                </div>
                <div className="grid sm:grid-cols-3 gap-2">
                  {[0, 1, 2].map((i) => (
                    <label key={i}
                           className={`flex items-center gap-2 px-3 py-2 rounded-md border border-dashed cursor-pointer transition-colors ${files[i] ? "border-[#2d6b6b] bg-[#e8f2f2]" : "border-[#cbbba0] hover:border-[#2d6b6b]"}`}
                           data-testid={`stores-quote-slot-${i + 1}`}>
                      <Paperclip size={14} className="text-[#2d6b6b] shrink-0" />
                      <div className="min-w-0 text-xs">
                        <div className="font-semibold text-[#1f2320]">Quote {i + 1}</div>
                        <div className="text-[#6e7770] truncate">
                          {files[i]?.name || "Choose file"}
                        </div>
                      </div>
                      <input type="file" className="hidden"
                             accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                             onChange={(e) => setFileAt(i, e.target.files?.[0])} />
                    </label>
                  ))}
                </div>
              </div>

              {filledFiles.length > 0 && (
                <label className="block">
                  <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                    Attachment Remark <span className="text-[#b3261e]">*</span>
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">min 10 chars</span>
                  </span>
                  <textarea value={attachmentRemark}
                            onChange={(e) => setAttachmentRemark(e.target.value.slice(0, 500))}
                            rows={2}
                            placeholder="Describe the vendor quotes / procurement sources."
                            className={`input-vgws min-h-[64px] ${trimmedAttRemark && !attRemarkOk ? "!border-[#b3261e]" : ""}`}
                            data-testid="stores-attachment-remark" />
                </label>
              )}
            </>
          )}

          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Remarks {remarksMandatory
                ? <span className="text-[#b3261e]">*</span>
                : <span className="text-[#6e7770] text-xs font-normal">(optional)</span>}
              {remarksMandatory && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">min 2 chars</span>}
            </span>
            <textarea ref={textareaRef} value={remarks}
                      onChange={(e) => setRemarks(e.target.value.slice(0, 1000))} rows={3}
                      placeholder={remarksMandatory ? "Describe your reason clearly…" : "Add any note for the record (optional)…"}
                      className={`input-vgws min-h-[80px] ${remarksMandatory && trimmedRemarks && !remarksOk ? "!border-[#b3261e]" : ""}`}
                      data-testid="stores-po-remarks" />
          </label>
        </div>

        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost" data-testid="stores-po-cancel">Cancel</button>
          <button onClick={submit} disabled={!canSubmit}
                  className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white border shadow-sm ${btnCls} ${!canSubmit ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid="stores-po-confirm">
            {busy
              ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <cfg.Icon size={16} />}
            {busy ? "Please wait…" : `Confirm ${cfg.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}
